import React, {useState} from 'react';
import {EditorContent, useEditor} from '@tiptap/react';
import {useDebouncedCallback} from 'use-debounce';
import {CodeBlockLowlight} from '@tiptap/extension-code-block-lowlight';
import StarterKit from '@tiptap/starter-kit';
import {all, createLowlight} from 'lowlight';
import css from 'highlight.js/lib/languages/css';
import html from 'highlight.js/lib/languages/xml';
import {useDispatch, useSelector} from 'react-redux';
import {CodeBlock} from '@tiptap/extension-code-block';
import {IElementHtml} from '../../../../incase-domain/elements/html';
import Modal from '../../../../incase-domain/components/modals/directory/Modal';
import {showHtmlCodeEditorModal} from '../../../../store/selectors/html.selectors';
import {showHtmlEditorModalAction} from '../../../../store/actions/html.actions';
import {formatHtml} from '../../../../utils/tipTap.utils';
import {useHtmlIframe} from '../../../../hooks/useHtmlIFrame';

interface HighlightedHtmlElementProps {
    element: IElementHtml;
    highlightedElementChangeHandler: {single: (field: keyof IElementHtml, value: unknown) => void};
}

const lowlight = createLowlight(all);
lowlight.register('html', html);
lowlight.register('css', css);

function HtmlEditorModal({highlightedElementChangeHandler, element}: HighlightedHtmlElementProps): JSX.Element {
    const [editorWidth, setEditorWidth] = useState(50);
    const openHtmlEditor = useSelector(showHtmlCodeEditorModal);
    const dispatch = useDispatch();

    const {containerRef} = useHtmlIframe(element);

    const debouncedChangeHandler = useDebouncedCallback((content: string) => {
        highlightedElementChangeHandler.single('text', content);
    }, 250);

    const editor = useEditor({
        extensions: [
            CodeBlockLowlight.configure({
                lowlight,
            }),
            StarterKit.configure({
                codeBlock: false,
            }),
            CodeBlock.configure({
                defaultLanguage: 'html',
                exitOnTripleEnter: false,
                exitOnArrowDown: false,
            }),
        ],
        content: formatHtml(element?.text || ''),
        onUpdate: ({editor}) => {
            const updatedContent = editor.getText();
            if (debouncedChangeHandler) debouncedChangeHandler(updatedContent);
        },
        autofocus: 'end',
        onCreate: ({editor}) => {
            editor.commands.setCodeBlock();
        },
        editorProps: {
            attributes: {
                class: 'prose prose-sm focus:outline-none',
                spellcheck: 'false',
            },
        },
    });

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
        e.preventDefault();

        const startX = e.clientX;
        const initialEditorWidth = editorWidth;

        const handleMouseMove = (moveEvent: MouseEvent): void => {
            const deltaX = moveEvent.clientX - startX;
            const newEditorWidth = Math.min(Math.max(initialEditorWidth + (deltaX / window.innerWidth) * 100, 10), 90);
            setEditorWidth(newEditorWidth);
        };

        const handleMouseUp = (): void => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    };

    return (
        <Modal
            isOpen={openHtmlEditor}
            onCancel={() => {
                dispatch(showHtmlEditorModalAction());
                editor?.destroy();
            }}
            size="fit"
        >
            <div className="c-code_editor_modal">
                <div
                    style={{
                        width: `${editorWidth}%`,
                    }}
                    className="c-code_editor_modal__code-editor"
                >
                    <EditorContent editor={editor} className="c-code_editor ql-editor" />
                </div>

                <div className="c-code_editor_modal__line-divider" onMouseDown={handleMouseDown} />

                <div
                    style={{
                        width: `${100 - editorWidth}%`,
                    }}
                    className="c-code_editor_modal__html-preview"
                >
                    <div ref={containerRef} />
                </div>
            </div>
        </Modal>
    );
}

export default HtmlEditorModal;
